/**
 * WaitlistView - Presentational component for the waitlist confirmation page
 * @storybook See WaitlistView.stories.tsx for usage examples
 */

import { t } from "@travily/i18n";
import type { FoundingMemberStatus } from "@travily/types/models/Enums";
import { ResponsiveImage } from "@ui/components/ResponsiveImage";
import { RetroButton } from "@ui/components/RetroButton";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { useHeightBreakpoint } from "@ui/hooks/use-height-breakpoint";
import successCheckmark2x from "./assets/success-checkmark@2x.webp";
import successCheckmark3x from "./assets/success-checkmark@3x.webp";
import successCheckmark from "./assets/success-checkmark.webp";
import successCheckmarkMobile2x from "./assets/success-checkmark-mobile@2x.webp";
import successCheckmarkMobile3x from "./assets/success-checkmark-mobile@3x.webp";
import successCheckmarkMobile from "./assets/success-checkmark-mobile.webp";
import { QueueAnimation } from "./components/QueueAnimation";

export interface WaitlistViewProps {
  foundingMemberType?: string | null;
  foundingMemberStatus?: FoundingMemberStatus | null;
  onContinueFoundingMember?: () => void;
  onShare?: () => void;
}

function formatMemberType(type: string | null | undefined): string {
  if (!type) return t("web.waitlists.show.member_fallback");
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function getSubtitleText(status: FoundingMemberStatus | null): string {
  switch (status) {
    case "in_progress":
    case "paused":
      return t("web.waitlists.show.subtitle.in_progress");
    case "completed":
      return t("web.waitlists.show.subtitle.completed");
    default:
      return t("web.waitlists.show.subtitle.default");
  }
}

function getFoundingMemberButtonText(
  status: FoundingMemberStatus | null,
  memberType: string | null | undefined,
): string {
  const formattedType = formatMemberType(memberType);
  switch (status) {
    case "in_progress":
    case "paused":
    case "skipped":
      return t("web.waitlists.show.founding_member_button.continue", { type: formattedType });
    default:
      return t("web.waitlists.show.founding_member_button.default");
  }
}

export function WaitlistView({
  foundingMemberType,
  foundingMemberStatus,
  onContinueFoundingMember,
  onShare,
}: WaitlistViewProps) {
  const isXlOrAbove = useBreakpoint("xl");
  const isAboveHLg = useHeightBreakpoint("xl");

  const showFoundingMemberButton = foundingMemberStatus !== "completed" && onContinueFoundingMember;
  const subtitleText = getSubtitleText(foundingMemberStatus ?? null);
  const foundingButtonText = getFoundingMemberButtonText(foundingMemberStatus ?? null, foundingMemberType);

  return (
    <div className="relative flex w-full max-w-container min-h-[calc(350px_+_min(31vw,534px))] max-h-[calc(100dvh_-_88px_-_1rem)] flex-col rounded-5xl bg-white shadow-[inset_0_0_0_1px_#e5e7dd,inset_0_-1px_0_0_var(--color-green-200)]">
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 lg:pt-6 pb-[35%] lg:-mb-6 text-center">
        <div className="mb-4 md:mb-3 lg:mb-2 lg:h-xl:mb-3">
          <ResponsiveImage
            mobile={{
              src: successCheckmarkMobile,
              src2x: successCheckmarkMobile2x,
              src3x: successCheckmarkMobile3x,
            }}
            desktop={{
              src: successCheckmark,
              src2x: successCheckmark2x,
              src3x: successCheckmark3x,
            }}
            alt={t("web.waitlists.show.checkmark_alt")}
            mobileWidth={54}
            mobileHeight={54}
            desktopWidth={96}
            desktopHeight={96}
            className="size-14 md:h-lg:size-20 md:h-xl:size-24"
          />
        </div>

        <h1 className="text-display-sm md:h-lg:text-display-lg text-green-950">{t("web.waitlists.show.title")}</h1>

        <p className="mt-3 max-w-4xl text-base md:h-lg:text-xl xl:h-xl:text-2xl text-green-950">{subtitleText}</p>

        {(showFoundingMemberButton || onShare) && (
          <div className="relative z-10 mt-6 flex flex-col gap-3 sm:flex-row sm:gap-4">
            {showFoundingMemberButton && (
              <RetroButton
                color="yellow"
                size={isXlOrAbove && isAboveHLg ? "48" : "40"}
                onPress={onContinueFoundingMember}
              >
                {foundingButtonText}
              </RetroButton>
            )}
            {onShare && (
              <RetroButton
                color={showFoundingMemberButton ? "green" : "yellow"}
                size={isXlOrAbove && isAboveHLg ? "48" : "40"}
                onPress={onShare}
              >
                {t("web.waitlists.show.share_button")}
              </RetroButton>
            )}
          </div>
        )}
      </div>

      <div className="absolute inset-x-px bottom-px z-0">
        <QueueAnimation alt={t("web.waitlists.show.queue_alt")} className="rounded-b-[39px]" />
      </div>

      <div className="pointer-events-none absolute inset-0 rounded-5xl shadow-[inset_0_0_0_1px_#e5e7dd,inset_0_-1px_0_0_var(--color-green-200)]" />
    </div>
  );
}
